import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {
    recommendationMeta,
    renderCircuitUpgradeAnalysis,
    summarizeCircuitUpgradeAnalysis,
} from "../../helpers/circuit_upgrade_analysis";
import {renderUpgradeStrip, UPGRADE_NEUTRAL_ICON} from "../../helpers/upgrade_analysis_ui";

const UPGRADE_RECOMMENDATIONS = new Set(["ReallyNeedsUpgrade", "CouldUseUpgrade"]);
// These are real analysis outcomes (not "no upgrade needed"), so the strip
// stays visible in a muted style to explain why there is no recommendation.
const UNAVAILABLE_RECOMMENDATIONS = new Set(["NoUsablePlanLimits", "AnalysisUnavailable"]);

// 64-bit ids arrive from CBOR as JS numbers and can lose their low digits, so
// compare exactly first, then numerically (which rounds the URL id the same way).
function idsMatch(a, b) {
    if (a === null || a === undefined || b === null || b === undefined) {
        return false;
    }
    if (String(a) === String(b)) {
        return true;
    }
    const na = Number(a);
    const nb = Number(b);
    return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

export class ShaperCircuitUpgradeAnalysis extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastAnalysis = null;
    }

    title() {
        return "Upgrade Analysis";
    }

    channelRequests() {
        return ["CircuitUpgradeAnalysis"];
    }

    buildContainer() {
        const base = super.buildContainer();
        this.container = base;
        base.classList.add("lqos-upgrade-card");
        const title = base.querySelector(".dashbox-title");
        if (title) title.classList.add("d-none");
        // Only shown when this circuit is recommended for upgrade.
        base.style.display = "none";
        const body = document.createElement("div");
        body.id = `circuitUpgradeAnalysis_${this.slotNumber}`;
        body.setAttribute("aria-busy", "true");
        this.contentId = body.id;
        base.appendChild(body);
        return base;
    }

    setVisible(visible) {
        if (this.container) {
            this.container.style.display = visible ? "" : "none";
        }
    }

    onLoadStart(channels) {
        if (!Array.isArray(channels) || !channels.includes("CircuitUpgradeAnalysis")) {
            return;
        }
        this.setVisible(false);
        const body = document.getElementById(this.contentId);
        if (body) {
            body.innerHTML = "";
            body.setAttribute("aria-busy", "true");
        }
    }

    onScopeChange() {
        this.lastAnalysis = null;
        this.onLoadStart(["CircuitUpgradeAnalysis"]);
    }

    canExplain() {
        // Only participate when this circuit is recommended for upgrade.
        return this.lastAnalysis !== null
            && UPGRADE_RECOMMENDATIONS.has(this.lastAnalysis?.recommendation);
    }

    isExplainReady() {
        return this.lastAnalysis !== null;
    }

    explain() {
        if (!this.lastAnalysis) return null;
        return {
            dashlet_name: this.title(),
            description: summarizeCircuitUpgradeAnalysis(this.lastAnalysis),
        };
    }

    currentRequestId() {
        const requestId = globalThis.window?.circuitUpgradeAnalysisRequestId;
        const numeric = Number(requestId);
        return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
    }

    payloadMatchesCurrentScope(payload) {
        const currentCircuitId = globalThis.window?.circuitId;
        if (currentCircuitId !== undefined) {
            if (payload?.circuit_hash === undefined || payload?.circuit_hash === null) {
                return false;
            }
            if (!idsMatch(payload.circuit_hash, currentCircuitId)) {
                return false;
            }
        }

        const currentRequestId = this.currentRequestId();
        if (currentRequestId === null) {
            return true;
        }
        if (payload?.request_id === undefined || payload?.request_id === null) {
            // Older webui backends emitted scoped upgrade-analysis payloads before
            // request_id existed. Accept them only after the circuit_hash check
            // above, so mixed-version deployments keep refreshing without widening
            // the payload scope.
            return true;
        }
        const payloadRequestId = Number(payload?.request_id);
        if (Number.isInteger(payloadRequestId)) {
            return payloadRequestId === currentRequestId;
        }

        return false;
    }

    onMessage(msg) {
        if (msg?.Data?.target !== "circuit_upgrade_analysis") return;
        const payload = msg.Data.blob || {};
        if (!this.payloadMatchesCurrentScope(payload)) return;

        const body = document.getElementById(this.contentId);
        if (!body) return;

        this.lastAnalysis = payload;
        const recommendation = payload?.recommendation;
        const circuitName = payload?.circuit_name
            || `Circuit ${payload?.circuit_hash ?? ""}`.trim();

        if (!UPGRADE_RECOMMENDATIONS.has(recommendation)) {
            if (UNAVAILABLE_RECOMMENDATIONS.has(recommendation)) {
                const unavailableMeta = recommendationMeta(recommendation);
                this.setVisible(true);
                body.setAttribute("aria-busy", "false");
                renderUpgradeStrip(body, {
                    icon: unavailableMeta.icon,
                    badgeLabel: unavailableMeta.title,
                    headline: unavailableMeta.summary,
                    muted: true,
                    modalTitle: `Upgrade Analysis — ${circuitName}`,
                    buildBody: () => {
                        const holder = document.createElement("div");
                        renderCircuitUpgradeAnalysis(holder, payload, { neutralBadge: true });
                        return holder.innerHTML;
                    },
                });
                this.rendered = true;
                return;
            }
            this.setVisible(false);
            body.innerHTML = "";
            body.setAttribute("aria-busy", "false");
            return;
        }

        const meta = recommendationMeta(recommendation);

        this.setVisible(true);
        body.setAttribute("aria-busy", "false");
        renderUpgradeStrip(body, {
            icon: UPGRADE_NEUTRAL_ICON,
            badgeLabel: meta.title,
            headline: circuitName,
            modalTitle: `Upgrade Analysis — ${circuitName}`,
            buildBody: () => {
                const holder = document.createElement("div");
                renderCircuitUpgradeAnalysis(holder, payload, { neutralBadge: true });
                return holder.innerHTML;
            },
        });
        this.rendered = true;
    }
}
